/* Interaction tracking for the CSA B139 / National Fire Code page.
 *
 * The theme's analytics layer is Google Tag Manager: layout/theme.liquid loads
 * two containers, and GA4 and Google Ads are both fired from inside them. So
 * this file pushes to window.dataLayer and stops there. It loads no library,
 * sets no cookie and talks to no endpoint of its own; a tag in GTM decides what,
 * if anything, becomes a hit.
 *
 * Every listener is delegated off document rather than bound to elements. That
 * is what lets one file cover five sections without editing any of them, which
 * matters because three of those sections are shared with pages that are
 * already live. It also means markup swapped in by the theme editor, or cards
 * cloned by the marquee script after load, are covered without re-running
 * anything.
 *
 * Ordering note: the accordion and disclosure scripts bind their handlers
 * directly to their buttons, so those run in the target phase, before this file
 * sees the click bubble up to document. aria-expanded is therefore already the
 * post-click state by the time it is read below, which is why "did this open"
 * is a plain read rather than an inversion of the previous value.
 */

(() => {
  if (window.__b139AnalyticsReady) return;
  window.__b139AnalyticsReady = true;

  window.dataLayer = window.dataLayer || [];

  const push = (payload) => {
    window.dataLayer.push(payload);
  };

  /* Buttons wrap their label in a wordmark span that splits the text into
     per-character nodes for the styling, so textContent comes back with the
     right letters but no reliable spacing. Collapsing whitespace here keeps the
     value readable in a GA4 report. */
  const labelOf = (element) => (element.textContent || '').replace(/\s+/g, ' ').trim();

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    /* --- Lead-form CTAs, hero and final ------------------------------------
       Location is read off an ancestor rather than passed in as an attribute,
       because the two sections that render these buttons are shared and were
       left untouched. Type comes from the panel the button opens: "contact" is
       the primary Book an Inspection action in both places, "download" is the
       secondary one. */
    const cta = target.closest('[data-lead-open]');
    if (cta) {
      let location = 'other';
      if (cta.closest('.igrid-cta')) location = 'final_cta';
      else if (cta.closest('.banner')) location = 'hero';

      push({
        event: 'cta_click',
        cta_location: location,
        cta_type: cta.dataset.leadOpen === 'contact' ? 'primary' : 'secondary',
        cta_label: labelOf(cta),
      });
      return;
    }

    /* --- Who This Is For category row -------------------------------------
       The cards are not links, so the interactions worth counting are the two
       arrow buttons and a click on a card itself. Auto-scroll is deliberately
       not reported: it happens without the visitor doing anything, and counting
       it would drown the deliberate interactions it is mixed in with. */
    const marqueeNav = target.closest('[data-b139-marquee-prev], [data-b139-marquee-next]');
    if (marqueeNav) {
      push({
        event: 'category_card_interaction',
        interaction: marqueeNav.hasAttribute('data-b139-marquee-next') ? 'next' : 'previous',
      });
      return;
    }

    const card = target.closest('.b139-audience__card');
    if (card) {
      const label = card.querySelector('.b139-audience__label');
      push({
        event: 'category_card_interaction',
        interaction: 'card_click',
        category_label: label ? labelOf(label) : '',
      });
      return;
    }

    /* --- "View what we inspect" disclosure ---------------------------------
       Only the opening half is reported. A close is the visitor undoing the
       thing that was interesting, and counting both would double every
       engagement from anyone who tidies up after themselves. */
    const disclosure = target.closest('[data-b139-disclosure-toggle]');
    if (disclosure) {
      if (disclosure.getAttribute('aria-expanded') === 'true') {
        const label = disclosure.querySelector('.b139-disclosure__label');
        push({
          event: 'disclosure_open',
          disclosure_label: labelOf(label || disclosure),
        });
      }
      return;
    }

    /* --- FAQ ---------------------------------------------------------------
       The accordion closes the other items when one opens, but only the item
       that was clicked is reported; the closes are a side effect of this
       question being chosen, not separate decisions.

       Position is counted across the whole list rather than within a column, so
       the number keeps meaning "the nth question" if the two-column layout ever
       changes or collapses on mobile. */
    const faq = target.closest('[data-igrid-faq-trigger]');
    if (faq) {
      if (faq.getAttribute('aria-expanded') === 'true') {
        const item = faq.closest('[data-igrid-faq-item]');
        const list = faq.closest('[data-igrid-faq-accordion]');
        const items = list ? Array.from(list.querySelectorAll('[data-igrid-faq-item]')) : [];
        const question = faq.querySelector('span');

        push({
          event: 'faq_open',
          faq_question: labelOf(question || faq),
          faq_position: item && items.length ? items.indexOf(item) + 1 : null,
        });
      }
    }
  });
})();
